import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Radio, MapPin, WifiOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const PING_MS = 60000; // ping the backend every 60s while active

/**
 * GoActiveToggle — dashboard widget for Distributing Partners. When toggled on,
 * the browser Geolocation API supplies a coordinate that we POST to
 * /me/ep-active and refresh every 60 seconds so HQ can see the DP live on
 * the dispatch map. Renders nothing when the signed-in user isn't linked to
 * a DP profile (so it never shows up for admins/personnel/clients).
 */
export default function GoActiveToggle() {
  const [linked, setLinked] = useState(null); // null = still loading
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coords, setCoords] = useState(null);
  const [lastPing, setLastPing] = useState(null);
  const [error, setError] = useState("");
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const latestCoordsRef = useRef(null);

  // Hydrate current linkage + active state
  useEffect(() => {
    api.get("/me/ep-active")
      .then((r) => {
        setLinked(!!r.data?.linked);
        setActive(!!r.data?.active);
        setLastPing(r.data?.last_ping_at || null);
      })
      .catch(() => setLinked(false));
  }, []);

  const stopStreaming = useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation?.clearWatch) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch { /* noop */ }
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    watchIdRef.current = null;
  }, []);

  const postPing = useCallback(async () => {
    const c = latestCoordsRef.current;
    if (!c) return;
    try {
      await api.post("/me/ep-active", { active: true, lat: c.lat, lng: c.lng });
      setLastPing(new Date().toISOString());
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Ping failed");
    }
  }, []);

  const startStreaming = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setError("Your browser doesn't support Geolocation.");
      return false;
    }
    // Get an initial fix first, then continue watching + set the 60s cadence.
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        latestCoordsRef.current = c;
        setCoords(c);
        try {
          await api.post("/me/ep-active", { active: true, lat: c.lat, lng: c.lng });
          setActive(true);
          setLastPing(new Date().toISOString());
          toast.success("You're now visible on HQ dispatch");
        } catch (err) {
          setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to go active");
          setActive(false);
          return;
        }
        // Continuous watch so we always have the freshest reading between pings.
        watchIdRef.current = navigator.geolocation.watchPosition(
          (p) => {
            const nc = { lat: p.coords.latitude, lng: p.coords.longitude };
            latestCoordsRef.current = nc;
            setCoords(nc);
          },
          () => { /* ignore transient errors — the 60s ping will retry */ },
          { enableHighAccuracy: true, maximumAge: 30000, timeout: 20000 },
        );
        intervalRef.current = setInterval(postPing, PING_MS);
      },
      (err) => {
        setError(err.message || "Location permission denied");
        setActive(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
    return true;
  }, [postPing]);

  const goOffline = useCallback(async () => {
    setBusy(true);
    stopStreaming();
    try {
      await api.post("/me/ep-active", { active: false });
      setActive(false);
      setCoords(null);
      setLastPing(null);
      setError("");
      toast.success("You're offline — HQ dispatch won't see you");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to go offline");
    } finally {
      setBusy(false);
    }
  }, [stopStreaming]);

  const onToggle = async (next) => {
    setError("");
    if (next) {
      setBusy(true);
      startStreaming();
      setBusy(false);
    } else {
      await goOffline();
    }
  };

  // Cleanup on unmount so we don't leak the watcher when the user navigates.
  useEffect(() => () => stopStreaming(), [stopStreaming]);

  if (linked !== true) return null;

  return (
    <Card
      className={`p-5 mb-4 border ${active ? "border-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-white" : "border-neutral-200 bg-white"}`}
      data-testid="go-active-card"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-emerald-500 text-white" : "bg-neutral-100 text-neutral-500"}`}>
          {active ? (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
            </span>
          ) : (
            <WifiOff size={18} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="label-eyebrow flex items-center gap-1.5">
            <Radio size={11} /> Live Dispatch Status
          </p>
          <p className="text-sm font-semibold text-[var(--ink)]">
            {active ? "You're active — HQ can dispatch nearest-order to you" : "You're offline — HQ can't see your location"}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {active
              ? `Sharing location every ${PING_MS / 1000}s.${coords ? ` Last fix: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}` : ""}`
              : "Flip on to receive nearest-order dispatches from HQ."}
          </p>
          {error && (
            <p className="text-xs text-red-600 mt-1" data-testid="go-active-error">{error}</p>
          )}
          {active && lastPing && (
            <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
              <MapPin size={9} /> Last ping · {new Date(lastPing).toLocaleTimeString()}
            </p>
          )}
        </div>
        <Switch
          checked={active}
          disabled={busy}
          onCheckedChange={onToggle}
          data-testid="go-active-switch"
          aria-label="Go active"
        />
      </div>
    </Card>
  );
}
